import { theme } from 'antd';
import {
  type CSSProperties,
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import {
  Link,
  type LinkProps,
  type NavigateOptions,
  type To,
  useHref,
  useLinkClickHandler,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { preloadRoute } from '@/config/preloadRoute';
import { useShellText } from '@/hooks/useShellText';
import styles from './navigationFeedback.module.css';

type Navigate = (to: To, options?: NavigateOptions) => void;
type NavigationAction = (navigate: Navigate) => void | Promise<void>;
type NavigationBlocker = () => Promise<boolean>;
type HistoryTraversal = {
  currentIndex: number;
  delta: number;
  targetIndex: number;
};

function getHistoryIndex(state: unknown): number | null {
  if (typeof state !== 'object' || state === null || !('idx' in state)) return null;
  return typeof state.idx === 'number' ? state.idx : null;
}

function getCurrentHistoryHref() {
  return window.location.pathname + window.location.search + window.location.hash;
}

const NavigationContext = createContext<{
  start: (action: () => void) => void;
  guard: (action: NavigationAction) => Promise<boolean>;
  register: (blocker: NavigationBlocker) => () => void;
} | null>(null);

function NavigationProgress() {
  const [visible, setVisible] = useState(false);
  const t = useShellText();
  const { token } = theme.useToken();
  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(true), 200);
    return () => window.clearTimeout(timer);
  }, []);
  if (!visible) return null;
  return (
    <div
      className={styles.progress}
      style={
        {
          '--route-progress-color': token.colorPrimary,
          '--route-progress-bg': token.colorPrimaryBg,
        } as CSSProperties
      }
      role="progressbar"
      aria-label={t('正在切换页面', 'Loading page')}
    >
      <span />
    </div>
  );
}

export function NavigationFeedback({ children }: PropsWithChildren) {
  const navigate = useNavigate();
  const [pending, startTransition] = useTransition();
  const [historyPending, setHistoryPending] = useState<string | null>(null);
  const blockers = useRef(new Set<NavigationBlocker>());
  const deciding = useRef(false);
  const currentHistoryIndex = useRef<number | null>(null);
  const restoringTraversal = useRef<HistoryTraversal | null>(null);
  const replayingTraversal = useRef<HistoryTraversal | null>(null);
  const register = useCallback((blocker: NavigationBlocker) => {
    blockers.current.add(blocker);
    return () => {
      blockers.current.delete(blocker);
    };
  }, []);
  useEffect(() => {
    function beforeUnload(event: BeforeUnloadEvent) {
      if (blockers.current.size) {
        event.preventDefault();
        event.returnValue = '';
      }
    }
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);
  const location = useLocation();
  const locationHref = useHref(location);
  useLayoutEffect(() => {
    currentHistoryIndex.current = getHistoryIndex(window.history.state);
    if (historyPending === locationHref) {
      setHistoryPending(null);
    }
  }, [historyPending, locationHref]);
  const t = useShellText();
  const navigateWithFeedback = useCallback(
    (to: To, options?: NavigateOptions) => {
      if (typeof to === 'string') preloadRoute(to);
      startTransition(() => {
        void navigate(to, options);
      });
    },
    [navigate],
  );
  const guard = useCallback(
    async (action: NavigationAction) => {
      if (!blockers.current.size) {
        await action(navigateWithFeedback);
        return true;
      }
      if (deciding.current) {
        return false;
      }
      deciding.current = true;
      try {
        for (const blocker of [...blockers.current]) {
          if (!(await blocker())) {
            return false;
          }
        }
        await action(navigateWithFeedback);
        return true;
      } finally {
        deciding.current = false;
      }
    },
    [navigateWithFeedback],
  );
  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      const targetIndex = getHistoryIndex(event.state);
      const replaying = replayingTraversal.current;
      if (replaying) {
        replayingTraversal.current = null;
        if (targetIndex === null || targetIndex === replaying.targetIndex) {
          currentHistoryIndex.current = targetIndex;
          setHistoryPending(getCurrentHistoryHref());
          return;
        }
      }

      const restoring = restoringTraversal.current;
      if (restoring) {
        event.stopImmediatePropagation();
        restoringTraversal.current = null;
        if (targetIndex !== null && targetIndex !== restoring.currentIndex) return;
        void guard(() => {
          replayingTraversal.current = restoring;
          window.history.go(restoring.delta);
        });
        return;
      }

      const currentIndex = currentHistoryIndex.current;
      if (!blockers.current.size || currentIndex === null || targetIndex === null) {
        setHistoryPending(getCurrentHistoryHref());
        return;
      }

      const delta = targetIndex - currentIndex;
      if (delta === 0) {
        setHistoryPending(getCurrentHistoryHref());
        return;
      }

      event.stopImmediatePropagation();
      restoringTraversal.current = { currentIndex, delta, targetIndex };
      window.history.go(-delta);
    };
    window.addEventListener('popstate', onPopState, true);
    return () => {
      restoringTraversal.current = null;
      replayingTraversal.current = null;
      window.removeEventListener('popstate', onPopState, true);
    };
  }, [guard]);
  const feedback = useMemo(
    () => ({ start: startTransition, guard, register }),
    [guard, register],
  );
  return (
    <NavigationContext.Provider value={feedback}>
      <span role="status" className={styles.announcement}>
        {pending || historyPending ? t('正在切换页面', 'Loading page') : ''}
      </span>
      {(pending || historyPending) && <NavigationProgress />}
      {children}
    </NavigationContext.Provider>
  );
}

export function useFeedbackNavigate(): Navigate {
  const navigate = useNavigate();
  const context = useContext(NavigationContext);
  return useCallback(
    (to: To, options?: NavigateOptions) => {
      if (!context) {
        void navigate(to, options);
        return;
      }
      void context.guard(() => {
        if (typeof to === 'string') {
          preloadRoute(to);
        }
        context.start(() => {
          void navigate(to, options);
        });
      });
    },
    [context, navigate],
  );
}

/** Confirm before mutating shell state; the callback receives approved navigation. */
export function useGuardedNavigation() {
  const context = useContext(NavigationContext);
  const navigate = useNavigate();
  return useCallback(
    async (action: NavigationAction) => {
      if (context) {
        return context.guard(action);
      }
      await action((to, options) => {
        void navigate(to, options);
      });
      return true;
    },
    [context, navigate],
  );
}

export function useNavigationBlocker(enabled: boolean, blocker: NavigationBlocker) {
  const context = useContext(NavigationContext);
  useLayoutEffect(() => {
    if (enabled && context) {
      return context.register(blocker);
    }
  }, [enabled, context, blocker]);
}

/** Keep Link's modifier/new-tab/relative URL semantics while tracking transitions. */
export function FeedbackLink({
  onClick,
  to,
  target,
  replace,
  state,
  relative,
  preventScrollReset,
  reloadDocument,
  ...props
}: LinkProps) {
  const feedback = useContext(NavigationContext);
  const handleClick = useLinkClickHandler(to, {
    target,
    replace,
    state,
    relative,
    preventScrollReset,
  });
  return (
    <Link
      {...props}
      to={to}
      target={target}
      replace={replace}
      state={state}
      relative={relative}
      preventScrollReset={preventScrollReset}
      reloadDocument={reloadDocument}
      onClick={(event) => {
        onClick?.(event);
        const external =
          typeof to === 'string' && /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(to);
        if (
          !feedback ||
          reloadDocument ||
          external ||
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey ||
          (target && target !== '_self') ||
          props.download != null
        ) {
          return;
        }
        event.preventDefault();
        void feedback.guard(() => feedback.start(() => handleClick(event)));
      }}
    />
  );
}
